// MappedIn Steps Utility
import { getMapData } from "@mappedin/mappedin-js";
import { getStepByStepInstructions } from "@mappedin/step-by-step/legacy";
import axios from "axios";

const DIRECTIONS_EN = {
  "directions.depart-from": "Leave {location}",
  "directions.depart-from-loc": "Leave your location",
  "directions.depart-turn": "Start heading {bearing}",
  "directions.depart-back": "Turn around and start moving",
  "directions.turn": "Turn {bearing}",
  "directions.turn-at": "Turn {bearing} at {location}",
  "directions.turn-at-gate": "Turn {bearing} at Gate {location}",
  "directions.turn-around": "Turn around",
  "directions.continue": "Head {bearing}",
  "directions.continue-straight": "And continue straight",
  "directions.combine": "and",
  "directions.bearing-straight": "straight",
  "directions.bearing-left": "left",
  "directions.bearing-right": "right",
  "directions.bearing-slight-left": "slightly left",
  "directions.bearing-slight-right": "slightly right",
  "directions.take-vortex": "Take {vortex} {direction} to {target}",
  "directions.take-vortex-notarget": "Take {vortex}",
  "directions.take-vortex-noverticality": "Take {vortex} to {target}",
  "directions.vortex-up": "up",
  "directions.vortex-down": "down",
  "directions.vortex-escalator": "escalator",
  "directions.vortex-elevator": "elevator",
  "directions.vortex-stairs": "stairs",
  "directions.vortex-exit-escalator": "Exit escalator",
  "directions.vortex-exit-elevator": "Exit elevator",
  "directions.vortex-exit-stairs": "Exit stairs",
  "directions.arrive-at": "Arrive at {location}",
  "directions.arrive-at-des": "Arrive at your destination",
  "directions.to-loc": "Directions to {location}",
  "directions.to-gate-loc": "Directions to Gate {location}",
  "directions.to-location": "To {location}",
  "directions.towards-location": "Towards {location}",
  "directions.towards-location-gate": "Towards Gate {location}",
  "directions.meters": "meters",
  "directions.feet-abbrev": "ft",
  "less-than-a-minute": "Less than a minute",
  "one-minute": "1 minute",
  min: "{minuteCount} min",
  minutes: "{minuteCount} minutes",
  "minutes-hour": "{hourCount} hr {minuteCount} mins",
  "minutes-hours": "{hourCount} hrs {minuteCount} mins",
  "directions.wait-time-estimated": "Estimated wait time {time} minutes",
  "directions.wait-time-estimated_one": "Estimated wait time 1 minute",
  "directions.wait-time-reason-default": "{reason} required to reach destination",
  "directions.wait-time-reason-security": "Security required to reach destination",
  "directions.wait-time-reason-shuttle": "Shuttle required to reach destination",
  "directions.vortex-exitvortex": "Exit {vortex}",
  "directions.vortex-exit-security": "Go through security",
  "directions.vortex-exit-ramp": "Exit ramp",
  "directions.vortex-door": "door",
  "directions.vortex-ramp": "ramp",
  "directions.vortex-portal": "portal",
  "directions.vortex-security": "security",
  "directions.vortex-shuttle": "shuttle",
  "directions.vortex-slide": "slide",
  "directions.vortex-ladder": "ladder",
};

// Fetch MappedIn configuration for a user
const fetchMappedInConfig = async (userId) => {
  console.log("userId", userId)
  if (!userId) {
    console.log("[MappedIn Steps] No userId provided");
    return null;
  }

  try {
    const response = await axios.get(`/api/wayfinder/mappedin-settings/${userId}`);
    if (response.data.success && response.data.data) {
      const data = response.data.data;
      console.log("data", data)
      if (data.enabled && data.apiKey && data.apiSecret && data.mapId) {
        return {
          key: data.apiKey,
          secret: data.apiSecret,
          mapId: data.mapId,
          mappedInStartLocation: data.mappedInStartLocation || '',
          mappedInStartLocationName: data.mappedInStartLocationName || '',
        };
      }
    }
    return null;
  } catch (error) {
    console.error("[MappedIn Steps] Error fetching config:", error);
    return null;
  }
};

export const fetchStepsFromMappedIn = async (destinationExternalId, spaceName, userId) => {

  console.log("userIddddd", userId, destinationExternalId)
  try {
    if (!destinationExternalId) {
      throw new Error("Destination external ID is required");
    }

    // Fetch dynamic configuration
    const config = await fetchMappedInConfig(userId);
console.log("config", config)

    if (!config) {
      throw new Error("MappedIn configuration not found");
    }

    const mapData = await getMapData(config);
    const locations = mapData.getByType("enterprise-location");
    
    // Find start location dynamically from settings
    let start = null;
    if (config.mappedInStartLocation) {
      start = locations.find((l) => 
        l.externalId === config.mappedInStartLocation || 
        l.name === config.mappedInStartLocationName
      );
    }
    
    // Fallback to hardcoded locations if not found
    if (!start) {
      start = locations.find((l) => l.name === "LOC-1IP30ZO0" || l.name === "Holographic Display");
    }
    
    if (!start) {
      throw new Error("Start location not found");
    }

    // Find destination by external ID
    const end = locations.find((l) => (l.externalId === destinationExternalId));
    if (!end) {
      throw new Error(`Destination with external ID '${destinationExternalId}' not found`);
    }

    const directions = await mapData.getDirections(start, end);
    if (!directions) {
      throw new Error("Could not get directions between locations");
    }

    const t = (key, options) => {
      let template = DIRECTIONS_EN[key] || key;
      if (options && typeof options === "object") {
        for (const [k, v] of Object.entries(options)) {
          const val = v != null ? String(v) : "";
          template = template.replace(new RegExp(`\\{${k}\\}`, "g"), val);
        }
      }
      return template;
    };

    const instructions = getStepByStepInstructions({
      mapData,
      directions,
      destination: undefined,
      options: {},
      formatOptions: {
        imperial: false,
        timeBased: false,
        language: "en",
        t,
      },
      locationContext: {},
    });

    if (!instructions || instructions.length === 0) {
      throw new Error("No step-by-step instructions found");
    }

    // Return both the total steps count and the step details
    return {
      totalSteps: instructions.length,
      steps: instructions.map((step, index) => ({
        stepNumber: index + 1,
        text: step.text,
        duration: 5, // Default 5 seconds per step
      })),
    };
  } catch (error) {
    console.error("Error fetching steps from MappedIn:", error);
    throw error;
  }
};